"use client";

import * as React from "react";
import { Search, FileSearch, Scale, Shield, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const methodologySteps = [{
  title: "Initial Inquiry",
  description: "Your journey begins with a friendly conversation. We listen to your needs, answer your questions about home care, and provide initial guidance on the best support options available for your loved one in Brighton & Hove.",
  icon: Search,
  titleClassName: "flex flex-col md:flex-row items-center gap-12 md:gap-24 !bg-[url(https://settled-jaguar.10web.cloud/wp-content/uploads/2026/05/3.jpeg)] flex flex-col md:flex-row items-center gap-12 md:gap-24 !bg-[url(https://settled-jaguar.10web.cloud/wp-content/uploads/2026/05/image_5-1.png)] flex flex-col md:flex-row items-center gap-12 md:gap-24 !bg-[url(https://settled-jaguar.10web.cloud/wp-content/uploads/2026/05/2.webp)]"
}, {
  title: "Free Home Assessment",
  description: "Our clinical lead visits your home to conduct a comprehensive, no-obligation assessment. We evaluate the living environment and specific health requirements to ensure every detail of safety and comfort is considered.",
  icon: FileSearch,
  titleClassName: "!bg-[url(https://settled-jaguar.10web.cloud/wp-content/uploads/2026/05/image_5-1.png)]"
}, {
  title: "Custom Care Plan",
  description: "We develop a bespoke care strategy tailored to your unique lifestyle and medical needs. This dynamic plan is designed to evolve with you, ensuring premium, compassionate support that respects your independence.",
  icon: Scale
}, {
  title: "Matching with a Caregiver",
  description: "We carefully select a professional caregiver whose skills and personality align with your preferences. This ensures a meaningful connection and consistent, high-quality care that feels like family.",
  icon: Shield
}];
export default function StrategicDefenseMethodology() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  const progressValue = methodologySteps.length > 1 ? (current + 1) / methodologySteps.length * 100 : 100;
  return <section data-section-id="253950" id="how-to-arrange-care" className="bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden relative bg-cover bg-center !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1665643/img/methodology_bg.png)]">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-lg mb-6">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-3xl md:text-5xl font-default font-bold mb-6 tracking-tight text-foreground">
            How to Arrange Care
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            A simple, four-step process to securing premium home care for your loved ones with Cynthylee Health Care.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex justify-between text-sm font-medium text-muted-foreground mb-4 px-1">
            <span className="font-mono">Phase 01</span>
            <span className="font-mono">Phase 0{methodologySteps.length}</span>
          </div>
          <Progress value={progressValue} className="h-1.5 bg-secondary" />
        </div>

        <Carousel setApi={setApi} opts={{
        align: "center",
        loop: false
      }} className="w-full max-w-5xl mx-auto">
          <CarouselContent>
            {methodologySteps.map((step, index) => {
            const isEvenNumber = (index + 1) % 2 === 0;
            return <CarouselItem key={index} data-index={index}>
                  <Card className="border-border bg-card/50 backdrop-blur-md shadow-lg hover:shadow-xl transition-shadow duration-500 overflow-hidden h-full rounded-2xl">
                    <CardContent className="p-8 md:p-16 h-full flex flex-col justify-center">
                      <div className={`${step.titleClassName || ""} flex flex-col md:flex-row items-center gap-12 md:gap-24 ${isEvenNumber ? "md:flex-row-reverse" : ""}`}>
                        {}
                        <div className={`flex-1 flex flex-col text-center items-center md:text-left md:items-start ${index === 0 ? "!bg-[rgba(0,0,0,0.5)] p-6 rounded-xl" : "!bg-[rgba(0,0,0,0.09)]"}`}>
                          <div className="mb-8 p-4 bg-primary/10 rounded-2xl inline-block">
                            <step.icon className="w-10 h-10 text-primary stroke-[1.5]" />
                          </div>
                          <div className="flex items-center gap-4 mb-4">
                            <span className="text-primary font-mono text-sm font-bold tracking-widest uppercase">
                              Step 0{index + 1}
                            </span>
                            <div className="h-px w-12 bg-primary/30" />
                          </div>
                          <h3 className={`text-3xl md:text-4xl font-default font-bold mb-6 ${index === 0 ? "text-white" : "text-card-foreground"}`}>
                            {step.title}
                          </h3>
                          <p className={`text-lg leading-relaxed font-default ${index === 0 ? "text-white/90" : "text-muted-foreground"}`}>
                            {step.description}
                          </p>
                        </div>

                        {}
                        <div className="flex-1 hidden md:flex justify-center items-center opacity-10">
                          <step.icon className="w-64 h-64 text-primary stroke-[0.5]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>;
          })}
          </CarouselContent>
          
          <div className="flex items-center justify-center gap-6 mt-12">
            <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all rounded-lg" />
            <div className="text-sm font-medium text-muted-foreground font-mono">
              {current + 1} / {methodologySteps.length}
            </div>
            <CarouselNext className="static translate-y-0 translate-x-0 size-12 border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all rounded-lg" />
          </div>
        </Carousel>

        <div className="mt-20 text-center">
          <Button asChild size="lg" className="rounded-lg px-8 h-14 text-base bg-primary hover:bg-primary/90 text-primary-foreground shadow-md">
            <Link to="/ContactPage">
              Start Your Care Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}